import { get, set } from '#~/persistence/index.js';

export type ShadowKind = 'first' | 're';

const memory = new Set<string>();
// Shadows whose `re` came from a previous launch. An overlapping bootstrap in
// this process reads `memory` and must still be allowed to report device_add.
const priorProcessShadows = new Set<string>();

const STORAGE_PREFIX = 'iris.device-shadow-first.';

// Before this PR the once-flag was a page localStorage key per camera, with no
// Studio in it. ITG browsers that ran that build still hold it.
const LEGACY_STORAGE_PREFIX = 'iris.eng4620.shadow-first.';

const onceKey = (deviceId: string, studioId: string | undefined): string =>
  `${studioId ?? ''}:${deviceId}`;

const legacyKey = (deviceId: string): string => `${LEGACY_STORAGE_PREFIX}${deviceId}`;

const hasLegacyFlag = (deviceId: string): boolean => {
  try {
    return (
      typeof localStorage !== 'undefined' && localStorage.getItem(legacyKey(deviceId)) !== null
    );
  } catch {
    return false;
  }
};

/**
 * What the stores said about one camera in one Studio, before anything is
 * recorded. Pass it to `commitShadowKind` right before `device_shadowed` is
 * sent.
 */
export type ShadowKindSource = 'memory' | 'stored' | 'legacy' | 'new';

export type ShadowKindReading = {
  readonly deviceId: string;
  readonly key: string;
  readonly kind: ShadowKind;
  // `memory` is a shadow committed by this process. `stored` and `legacy`
  // were written by an earlier launch.
  readonly source: ShadowKindSource;
};

/**
 * First-shadow vs re-shadow, step 1: read only.
 *
 * WHY: resets and replacement connections can shadow the camera again. Production 7d showed
 * 43 rows / 12 cameras (avg 3.58). Funnel "cameras shadowed" must count first
 * only; reconnects stay visible as `re`.
 *
 * Scope is the contract's: the first shadow of a `device_id` in this Studio on
 * this install. The once-flag lives in the app persistence layer, not page
 * localStorage: the desktop Studio page is served from a new localhost port on
 * every launch, so its localStorage is empty after each restart, while the
 * persistence layer survives launches. Another Studio gets its own `first`.
 * PostHog is not asked about first-ness.
 *
 * Reading records nothing. A bootstrap cancelled while this read is pending
 * (Studio switch, unmount) never sends `device_shadowed`, so it must not use up
 * the camera's `first`; `commitShadowKind` records it synchronously next to the
 * send instead.
 *
 * Upgrade (migration exception): the pre-upgrade flag is per browser and
 * camera and never recorded a Studio, so which Studio sent that camera's
 * `first` cannot be recovered. It keeps its old meaning: any Studio in this
 * browser answers `re` for that camera, exactly as the old build did. The
 * cost is that a camera already shadowed before the upgrade gets no new
 * per-Studio `first` in another Studio of this browser. The flag is never
 * removed, so no failed or interrupted write can lose this history. Cameras
 * without the old flag follow the per-Studio contract.
 *
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */
export const readShadowKind = async (
  deviceId: string,
  studioId?: string
): Promise<ShadowKindReading> => {
  const key = onceKey(deviceId, studioId);
  const reading = (kind: ShadowKind, source: ShadowKindSource): ShadowKindReading => ({
    deviceId,
    key,
    kind,
    source
  });
  if (memory.has(key)) {
    return reading('re', 'memory');
  }
  try {
    const stored = await get(`${STORAGE_PREFIX}${key}`);
    if (typeof stored === 'string') {
      return reading('re', 'stored');
    }
  } catch {
    // Unreadable storage: the session set in commitShadowKind is the only
    // evidence, and it says first.
  }
  return hasLegacyFlag(deviceId) ? reading('re', 'legacy') : reading('first', 'new');
};

/**
 * Remember a `re` that was stored before this process, so a later bootstrap
 * in the same launch does not treat that camera as a new add.
 */
export const rememberPriorProcessShadow = (shadow: ShadowKindReading): void => {
  if (shadow.source === 'stored' || shadow.source === 'legacy') {
    priorProcessShadows.add(shadow.key);
  }
};

export const isPriorProcessShadow = (key: string): boolean => priorProcessShadows.has(key);

/**
 * First-shadow vs re-shadow, step 2: record and answer. Synchronous, so a
 * caller that sends `device_shadowed` right after it cannot be cancelled in
 * between. Independent accepted reports that both read `first` are serialized
 * here. The broker accepts only one report per actual Proxy reset interval,
 * so an overlapping bootstrap in that same interval does not commit at all.
 */
export const commitShadowKind = (reading: ShadowKindReading): ShadowKind => {
  const { key, kind } = reading;
  if (memory.has(key)) {
    return 're';
  }
  memory.add(key);
  if (kind === 'first') {
    // Only a new first is written; nothing is ever deleted. A rejected write
    // leaves the session set as the only record, so the next launch can say
    // first again for this camera. That is the same storage-failure fallback
    // as before this change and is kept on purpose: the bootstrap must not
    // fail on storage, and there is no earlier record to lose.
    void set(`${STORAGE_PREFIX}${key}`, '1').catch(() => undefined);
  }
  return kind;
};

/** Read and commit in one step, for callers with no cancellation point. */
export const nextShadowKind = async (deviceId: string, studioId?: string): Promise<ShadowKind> =>
  commitShadowKind(await readShadowKind(deviceId, studioId));

/**
 * Test-only: wipe the in-memory once-flag so cases cannot leak across tests.
 * Persisted flags live in the persistence layer, which tests mock and clear.
 */
export const resetShadowKindForTests = (): void => {
  memory.clear();
  priorProcessShadows.clear();
};
